import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Download, Upload, Minimize2, Maximize2, Code, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';

export const JsMinifierTool: React.FC = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState<'minify' | 'beautify'>('minify');
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const minifyJs = (js: string) => {
    try {
      let minified = js;

      // Remove single-line comments (but preserve URLs and regex)
      minified = minified.replace(/\/\/(?![^\r\n]*["'`]).*$/gm, '');
      
      // Remove multi-line comments
      minified = minified.replace(/\/\*[\s\S]*?\*\//g, '');

      // Remove unnecessary whitespace
      minified = minified.replace(/\s+/g, ' ');
      
      // Remove spaces around operators and punctuation
      minified = minified.replace(/\s*([{}();,=+\-*/%<>!&|?:[\]])\s*/g, '$1');
      
      // Remove spaces around dots
      minified = minified.replace(/\s*\.\s*/g, '.');
      
      // Remove trailing semicolons before closing braces
      minified = minified.replace(/;}/g, '}');
      
      // Remove unnecessary semicolons
      minified = minified.replace(/;;+/g, ';');
      
      // Trim whitespace
      minified = minified.trim();

      setOutput(minified);
      setError('');
      return minified;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Minification failed';
      setError(errorMessage);
      setOutput('');
      return null;
    }
  };

  const beautifyJs = (js: string) => {
    try {
      let beautified = js;
      let indentLevel = 0;
      const indent = '  ';
      let result = '';
      let inString = false;
      let stringChar = '';
      let inRegex = false;
      let inComment = false;
      let commentType = '';

      for (let i = 0; i < beautified.length; i++) {
        const char = beautified[i];
        const nextChar = beautified[i + 1];
        const prevChar = beautified[i - 1];

        // Handle string literals
        if (!inComment && !inRegex && (char === '"' || char === "'" || char === '`')) {
          if (!inString) {
            inString = true;
            stringChar = char;
          } else if (char === stringChar && prevChar !== '\\') {
            inString = false;
            stringChar = '';
          }
        }

        // Handle regex literals (basic detection)
        if (!inString && !inComment && char === '/' && prevChar !== '*' && nextChar !== '/') {
          if (!inRegex && (prevChar === '=' || prevChar === '(' || prevChar === '[' || prevChar === ',' || prevChar === ':' || prevChar === ';' || prevChar === '!' || prevChar === '&' || prevChar === '|' || prevChar === '?' || prevChar === '+' || prevChar === '-' || prevChar === '*' || prevChar === '/' || prevChar === '%')) {
            inRegex = true;
          } else if (inRegex && prevChar !== '\\') {
            inRegex = false;
          }
        }

        // Handle comments
        if (!inString && !inRegex) {
          if (char === '/' && nextChar === '/') {
            inComment = true;
            commentType = '//';
          } else if (char === '/' && nextChar === '*') {
            inComment = true;
            commentType = '/*';
          } else if (inComment && commentType === '//' && char === '\n') {
            inComment = false;
            commentType = '';
          } else if (inComment && commentType === '/*' && char === '*' && nextChar === '/') {
            inComment = false;
            commentType = '';
            i++; // Skip the next character
            continue;
          }
        }

        if (inString || inRegex || inComment) {
          result += char;
          continue;
        }

        switch (char) {
          case '{':
            result += char + '\n';
            indentLevel++;
            result += indent.repeat(indentLevel);
            break;
          case '}':
            if (result.endsWith(indent)) {
              result = result.slice(0, -indent.length);
            }
            indentLevel = Math.max(0, indentLevel - 1);
            result += '\n' + indent.repeat(indentLevel) + char;
            if (nextChar && nextChar !== ';' && nextChar !== ',' && nextChar !== ')' && nextChar !== '}') {
              result += '\n' + indent.repeat(indentLevel);
            }
            break;
          case ';':
            result += char;
            if (nextChar && nextChar !== '}' && nextChar !== ')') {
              result += '\n' + indent.repeat(indentLevel);
            }
            break;
          case ',':
            result += char + ' ';
            break;
          case ':':
            result += char + ' ';
            break;
          default:
            result += char;
        }
      }

      // Clean up extra newlines and spaces
      result = result.replace(/\n\s*\n\s*\n/g, '\n\n');
      result = result.replace(/^\s+/gm, (match) => {
        const level = Math.floor(match.length / 2);
        return indent.repeat(level);
      });

      setOutput(result.trim());
      setError('');
      return result.trim();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Beautification failed';
      setError(errorMessage);
      setOutput('');
      return null;
    }
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    setError('');
    
    if (value.trim()) {
      if (mode === 'minify') {
        minifyJs(value);
      } else {
        beautifyJs(value);
      }
    } else {
      setOutput('');
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const downloadResult = () => {
    if (output) {
      const filename = mode === 'minify' ? t('tools.jsMinifier.minifiedFilename') : t('tools.jsMinifier.beautifiedFilename');
      const blob = new Blob([output], { type: 'application/javascript' });
      saveAs(blob, filename);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInput(content);
        if (mode === 'minify') {
          minifyJs(content);
        } else {
          beautifyJs(content);
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
  };

  const sampleJs = `// Sample JavaScript function
function calculateTotal(items) {
    let total = 0;
    
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        if (item.price && item.quantity) {
            total += item.price * item.quantity;
        }
    }
    
    return total;
}

// Event listener
document.addEventListener('DOMContentLoaded', function() {
    const button = document.getElementById('calculate-btn');
    
    button.addEventListener('click', function() {
        const items = [
            { price: 10.99, quantity: 2 },
            { price: 5.50, quantity: 1 },
            { price: 15.00, quantity: 3 }
        ];
        
        const total = calculateTotal(items);
        console.log('Total: $' + total.toFixed(2));
    });
});`;

  const useSample = () => {
    setInput(sampleJs);
    if (mode === 'minify') {
      minifyJs(sampleJs);
    } else {
      beautifyJs(sampleJs);
    }
  };

  const compressionStats = () => {
    if (!input || !output) return null;
    
    const originalSize = new Blob([input]).size;
    const compressedSize = new Blob([output]).size;
    const savings = originalSize - compressedSize;
    const percentage = ((savings / originalSize) * 100).toFixed(1);
    
    return {
      originalSize,
      compressedSize,
      savings,
      percentage: mode === 'minify' ? percentage : '0'
    };
  };

  const stats = compressionStats();

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Code className="w-5 h-5" />
            <span>{t('tools.jsMinifier.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              onClick={() => {
                setMode('minify');
                if (input) minifyJs(input);
              }}
              variant={mode === 'minify' ? 'primary' : 'secondary'}
            >
              <Minimize2 className="w-4 h-4 mr-2" />
              {t('tools.jsMinifier.minify')}
            </Button>
            <Button
              onClick={() => {
                setMode('beautify');
                if (input) beautifyJs(input);
              }}
              variant={mode === 'beautify' ? 'primary' : 'secondary'}
            >
              <Maximize2 className="w-4 h-4 mr-2" />
              {t('tools.jsMinifier.beautify')}
            </Button>
            <Button
              onClick={() => output && copyToClipboard(output)}
              disabled={!output}
              variant="outline"
            >
              <Copy className="w-4 h-4 mr-2" />
              {t('common.copy')}
            </Button>
            <Button
              onClick={downloadResult}
              disabled={!output}
              variant="outline"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('common.download')}
            </Button>
            <Button
              onClick={useSample}
              variant="outline"
            >
              {t('tools.jsMinifier.useSample')}
            </Button>
            <Button
              onClick={clearAll}
              variant="outline"
            >
              {t('common.clear')}
            </Button>
          </div>

          <div className="mb-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".js,.mjs,.jsx,.ts,.tsx"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="mb-2"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('common.selectFile')}
            </Button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800">
              <div className="flex items-center">
                <AlertTriangle className="w-4 h-4 text-red-500 mr-2" />
                <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Input/Output */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.jsMinifier.jsInput')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={input}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={t('tools.jsMinifier.inputPlaceholder')}
              className="min-h-[400px] font-mono text-sm"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{mode === 'minify' ? t('tools.jsMinifier.minifiedJs') : t('tools.jsMinifier.beautifiedJs')}</span>
              {output && (
                <span className="text-sm text-gray-500">
                  {output.split('\n').length} {t('tools.jsMinifier.lines')}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={output}
              readOnly
              placeholder={mode === 'minify' ? t('tools.jsMinifier.minifiedPlaceholder') : t('tools.jsMinifier.beautifiedPlaceholder')}
              className="min-h-[400px] font-mono text-sm bg-gray-50 dark:bg-gray-800"
            />
          </CardContent>
        </Card>
      </div>

      {/* Statistics */}
      {stats && (
        <Card>
          <CardHeader>
            <CardTitle>Compression Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">Original Size:</span>
                <div className="font-mono">{(stats.originalSize / 1024).toFixed(2)} KB</div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Output Size:</span>
                <div className="font-mono">{(stats.compressedSize / 1024).toFixed(2)} KB</div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Size Reduction:</span>
                <div className="font-mono">{(stats.savings / 1024).toFixed(2)} KB</div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Compression:</span>
                <div className="font-mono text-green-600">{stats.percentage}%</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Warning */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3 text-sm text-amber-700 dark:text-amber-400">
            <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Important Note:</p>
              <p>This is a basic JavaScript minifier. For production use, consider using professional tools like UglifyJS, Terser, or esbuild for better optimization and error handling.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
